"use client";

import { useState, useEffect } from "react";
import { addDays, eachDayOfInterval, format, isAfter, isBefore, startOfDay } from "date-fns";
import { BOOKING_RULES, latestReturnDate } from "@/lib/validation";
import { availableTimeOptions } from "@/lib/time-options";
import DatePopover from "./DatePopover";

interface BookingDateTimeProps {
  vehicleId: number;
  initialValue?: {
    date_from: string;
    date_to: string;
    pickup_time?: string;
    return_time?: string;
  } | null;
  onChange: (value: {
    date_from: string;
    date_to: string;
    pickup_time: string;
    return_time: string;
  } | null) => void;
}

interface BookingPeriod {
  date_from: string;
  date_to: string;
}

export default function BookingDateTime({ vehicleId, initialValue, onChange }: BookingDateTimeProps) {
  const [pickupDate, setPickupDate] = useState<Date | undefined>(
    initialValue?.date_from ? new Date(initialValue.date_from) : undefined,
  );
  const [returnDate, setReturnDate] = useState<Date | undefined>(
    initialValue?.date_to ? new Date(initialValue.date_to) : undefined,
  );
  const [pickupTime, setPickupTime] = useState(initialValue?.pickup_time ?? "12:00");
  const [returnTime, setReturnTime] = useState(initialValue?.return_time ?? "12:00");
  const [bookedDays, setBookedDays] = useState<Date[]>([]);
  const [loading, setLoading] = useState(true);

  const now = new Date();
  const today = startOfDay(now);
  const maxDate = addDays(now, BOOKING_RULES.ADVANCE_DAYS);
  // If every slot for today has already passed (late evening) today is no longer
  // bookable — push the calendar floor to tomorrow so the picker can't land on a
  // day with an empty time list.
  const earliestPickup =
    availableTimeOptions(today, now).length > 0 ? today : addDays(today, 1);
  const pickupTimeOptions = availableTimeOptions(pickupDate, now);
  const returnTimeOptions = availableTimeOptions(returnDate, now);

  // When the selected day is today, drop time slots already in the past; snap an
  // invalidated selection to the earliest remaining slot. The existing onChange
  // effect re-emits from the updated pickupTime/returnTime state.
  useEffect(() => {
    const opts = availableTimeOptions(pickupDate);
    if (opts.length > 0 && !opts.includes(pickupTime)) setPickupTime(opts[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupDate]);
  useEffect(() => {
    const opts = availableTimeOptions(returnDate);
    if (opts.length > 0 && !opts.includes(returnTime)) setReturnTime(opts[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnDate]);

  useEffect(() => {
    async function fetchBookings() {
      try {
        const res = await fetch(`/api/vehicles/availability?vehicle_id=${vehicleId}`);
        const bookings: BookingPeriod[] = await res.json();

        if (!Array.isArray(bookings)) {
          setBookedDays([]);
          return;
        }

        const days: Date[] = [];
        for (const b of bookings) {
          const interval = eachDayOfInterval({
            start: new Date(b.date_from),
            end: new Date(b.date_to),
          });
          days.push(...interval);
        }
        setBookedDays(days);
      } catch {
        setBookedDays([]);
      } finally {
        setLoading(false);
      }
    }

    fetchBookings();
  }, [vehicleId]);

  useEffect(() => {
    if (pickupDate && returnDate) {
      onChange({
        date_from: format(pickupDate, "yyyy-MM-dd"),
        date_to: format(returnDate, "yyyy-MM-dd"),
        pickup_time: pickupTime,
        return_time: returnTime,
      });
    } else {
      onChange(null);
    }
  }, [pickupDate, returnDate, pickupTime, returnTime, onChange]);

  function handlePickupSelect(date: Date | undefined) {
    setPickupDate(date);
    // Drop the return date if the new pickup makes it invalid: either earlier
    // than pickup, or past the first booked day after pickup (which would make
    // the rental straddle an occupied period).
    if (date && returnDate) {
      const rd = startOfDay(returnDate);
      const cap = startOfDay(latestReturnDate(date, bookedDays, maxDate));
      if (isBefore(rd, startOfDay(date)) || isAfter(rd, cap)) {
        setReturnDate(undefined);
      }
    }
  }

  function handleReturnSelect(date: Date | undefined) {
    setReturnDate(date);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
        <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mr-2" />
        Kraunamas kalendorius...
      </div>
    );
  }

  // A rental must be one contiguous free range, so the return date can't reach
  // past the first booked day that follows the pickup date. Without this cap the
  // picker greys out the booked days themselves but still lets the customer pick
  // a return on the far side of them, straddling an occupied period (the server
  // then rejects it with a 409 only after the whole form is filled in).
  const returnUpperBound = pickupDate
    ? latestReturnDate(pickupDate, bookedDays, maxDate)
    : maxDate;

  const returnDisabled = [
    { before: pickupDate ?? earliestPickup, after: returnUpperBound },
    ...bookedDays,
  ];

  return (
    <div className="space-y-3">
      <div className="space-y-3">
        {/* Pickup block */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Paėmimo data
          </label>
          <div className="flex items-center gap-2">
            <DatePopover
              value={pickupDate}
              onSelect={handlePickupSelect}
              disabled={[{ before: earliestPickup, after: maxDate }, ...bookedDays]}
            />

            <div className="w-px h-6 bg-gray-200 shrink-0" />

            <select
              value={pickupTimeOptions.includes(pickupTime) ? pickupTime : pickupTimeOptions[0] ?? pickupTime}
              onChange={(e) => setPickupTime(e.target.value)}
              className="px-2 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {pickupTimeOptions.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Return block */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Grąžinimo data
          </label>
          <div className="flex items-center gap-2">
            <DatePopover
              value={returnDate}
              onSelect={handleReturnSelect}
              disabled={returnDisabled}
              defaultMonth={pickupDate}
            />

            <div className="w-px h-6 bg-gray-200 shrink-0" />

            <select
              value={returnTimeOptions.includes(returnTime) ? returnTime : returnTimeOptions[0] ?? returnTime}
              onChange={(e) => setReturnTime(e.target.value)}
              className="px-2 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {returnTimeOptions.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-gray-300 inline-block" />
          Užimta
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-blue-500 inline-block" />
          Pasirinkta
        </div>
      </div>

    </div>
  );
}
